import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import CloudMapTenantView from '../cloudmap/CloudMapTenantView';
import OrganizationResourceView from '../cloudmap/OrganizationResourceView';
import AccountResourceView from '../cloudmap/AccountResourceView';
import { useTenant } from '../../context/TenantContext';
import Swal from 'sweetalert2';
import { Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

const CloudMapPage = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant, tenantID, organizationID, loading: tenantLoading, getAllAccounts } = useTenant();
  
  const [isScanning, setIsScanning] = useState(false);
  
  const [resources, setResources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [loadingText, setLoadingText] = useState('Loading CloudMap...');
  
  const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('accountId'));
  const [selectedOrgId, setSelectedOrgId] = useState(searchParams.get('orgId'));
  const [accountResources, setAccountResources] = useState([]);
  const [viewMode, setViewMode] = useState('tenant');
  
  // Get all accounts from tenant context
  const connectedAccounts = useMemo(() => getAllAccounts(), [tenant, getAllAccounts]);
  const cloudAccounts = useMemo(() => 
    connectedAccounts.map(acc => ({
      alias: acc.accountName,
      awsAccountId: acc.accountId,
      description: acc.accountName,
      iamrole: acc.iamRole
    })),
    [connectedAccounts]
  );

  // Fetch CloudMap data when tenant is loaded
  useEffect(() => {
    if (!tenantID) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      const apiUrl = process.env.REACT_APP_CLOUDMAP_API || 'https://9cdf5nf6ya.execute-api.us-west-2.amazonaws.com/prod';

      try {
        const [resourcesRes, summaryRes, trendsRes] = await Promise.all([
          fetch(`${apiUrl}/resources?tenantID=${tenantID}`),
          fetch(`${apiUrl}/summary?tenantID=${tenantID}`),
          fetch(`${apiUrl}/trends?tenantID=${tenantID}`)
        ]);

        const resourcesData = await resourcesRes.json();
        const summaryData = await summaryRes.json();
        const trendsDataRes = await trendsRes.json();

        setResources(resourcesData.resources || []);
        setSummary(summaryData);
        setTrendsData(trendsDataRes || []);
      } catch (error) {
        console.error('Error fetching CloudMap data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [tenantID, organizationID]);

  // Rotating loading messages for better UX
  useEffect(() => {
    if (isLoading || tenantLoading) {
      const messages = [
        'Loading CloudMap...',
        'Fetching cloud resources...',
        'Analyzing resource inventory...',
        'Discovering cloud assets...',
        'Preparing your resource map...'
      ];
      let index = 0;
      const interval = setInterval(() => {
        index = (index + 1) % messages.length;
        setLoadingText(messages[index]);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isLoading, tenantLoading]);

  // Update URL when account is selected
  useEffect(() => {
    if (selectedAccountId) {
      setSearchParams({ accountId: selectedAccountId });
    } else {
      setSearchParams({});
    }
  }, [selectedAccountId, setSearchParams]);

  const handleOrgSelect = (orgId) => {
    setSelectedOrgId(orgId);
    setViewMode('organization');
    setSearchParams({ orgId });
  };

  const handleStandaloneSelect = () => {
    setSelectedOrgId('standalone');
    setViewMode('organization');
    setSearchParams({ orgId: 'standalone' });
  };

  const handleAccountSelect = (accountId) => {
    setIsLoadingDetails(true);
    setSelectedAccountId(accountId);
    const filtered = resources.filter(r => r.metadata?.accountId === accountId);
    setAccountResources(filtered);
    setIsLoadingDetails(false);
    setViewMode('account');
  };
  
  const handleBackToOrganization = () => {
    setSelectedAccountId(null);
    setAccountResources([]);
    setViewMode('organization');
  };

  const handleBackToTenant = () => {
    setSelectedOrgId(null);
    setSelectedAccountId(null);
    setAccountResources([]);
    setViewMode('tenant');
    setSearchParams({});
  };

  const handleConnectAccount = () => {
    navigate('/dashboard/connected-accounts');
  };

  const handleScan = async () => {
    if (!tenantID) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Tenant data not loaded. Please refresh the page.',
      });
      return;
    }

    setIsScanning(true);

    try {
      const response = await fetch(
        'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/cloudmap/trigger',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantID }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        Swal.fire({
          icon: 'error',
          title: 'Scan Failed',
          text: errorData.message || 'Unknown error occurred.',
        });
        return;
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'CloudMap scan queued successfully! All accounts will be scanned across all regions.',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred while starting the scan.',
      });
    } finally {
      setIsScanning(false);
    }
  };

  const getUniqueAccountOptions = () => {
    try {
      if (!resources || !Array.isArray(resources)) {
        console.log('getUniqueAccountOptions: resources is not an array', resources);
        return [];
      }
      
      const accountsSet = new Map();
      
      resources.forEach(item => {
        if (item?.metadata?.accountId) {
          accountsSet.set(item.metadata.accountId, null);
        }
      });
      
      if (Array.isArray(cloudAccounts)) {
        cloudAccounts.forEach(account => {
          if (account?.awsAccountId && accountsSet.has(account.awsAccountId)) {
            accountsSet.set(account.awsAccountId, account.alias || account.description);
          }
        });
      }
      
      const result = Array.from(accountsSet.entries()).map(([accountId, alias]) => ({
        value: accountId || '',
        label: alias ? `${alias} (${accountId})` : (accountId || 'Unknown')
      }));
      
      console.log('getUniqueAccountOptions result:', result);
      return result;
    } catch (error) {
      console.error('Error in getUniqueAccountOptions:', error);
      return [];
    }
  };

  const getUniqueRegionOptions = () => {
    try {
      if (!resources || !Array.isArray(resources)) {
        console.log('getUniqueRegionOptions: resources is not an array', resources);
        return [];
      }
      
      const regionsSet = new Set();
      resources.forEach(item => {
        if (item?.region) {
          regionsSet.add(item.region);
        }
      });
      
      const result = Array.from(regionsSet).map(region => ({
        value: region || '',
        label: region || 'Unknown'
      }));
      
      console.log('getUniqueRegionOptions result:', result);
      return result;
    } catch (error) {
      console.error('Error in getUniqueRegionOptions:', error);
      return [];
    }
  };

  // Removed unused variables - account/region options not currently used



  if (tenantLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="alert alert-danger">Failed to load tenant data. Please refresh the page.</div>
      </div>
    );
  }

  if (connectedAccounts.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{
          backgroundImage: 'url("/images/blurred_image.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="text-center text-dark p-4" style={{ backgroundColor: 'rgba(170, 172, 242, 0.33)', borderRadius: '8px' }}>
          <h3>No Connected Accounts Found</h3>
          <p>
            To access this dashboard, please connect at least one account. Click the button below to get started.
          </p>
          <button className="btn text-light mt-3" style={{ backgroundColor: '#342ead' }} onClick={handleConnectAccount}>
            Connect Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="cloudmap-page container-fluid pb-5"
      style={{
        transition: 'margin-left 0.3s ease',
        marginLeft: isSidebarOpen ? '0px' : '0',
      }}
    >
      <style>{`
        .cloudmap-page .btn-scan {
          background-color: #342ead;
          color: white;
          border: none;
          padding: 8px 24px;
          border-radius: 6px;
          font-weight: 500;
        }
        .cloudmap-page .btn-scan:hover {
          background-color: #2a2390;
        }
        .cloudmap-page .react-select__control {
          border-radius: 6px;
        }
      `}</style>
      {isLoading ? (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)', paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3" style={{ color: '#342ead', fontSize: '1.1rem' }}>{loadingText}</p>
        </div>
      ) : (
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>CloudMap</h2>
              <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: 0 }}>
                {selectedAccountId
                  ? 'Detailed resource inventory for individual account'
                  : 'Comprehensive cloud resource discovery and inventory management'
                }
              </p>
            </div>
            <button
              className="btn"
              style={{
                backgroundColor: '#6D5BFF',
                color: 'white',
                fontWeight: '600',
                padding: '10px 24px',
                borderRadius: '8px',
                border: 'none'
              }}
              onClick={handleScan}
              disabled={isScanning}
            >
              {isScanning ? 'Scanning...' : 'Scan Now'}
            </button>
          </div>

          {isLoadingDetails ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
              <div className="spinner-border" style={{ color: '#6D5BFF' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : viewMode === 'account' ? (
            <AccountResourceView
              accountId={selectedAccountId}
              resources={accountResources}
              cloudAccounts={cloudAccounts}
              onBack={handleBackToOrganization}
            />
          ) : viewMode === 'organization' ? (
            <OrganizationResourceView
              resources={resources.filter(r => {
                const accountId = r.metadata?.accountId;
                if (selectedOrgId === 'standalone') {
                  const standaloneAccounts = tenant?.cloudAccounts?.aws || [];
                  return standaloneAccounts.some(acc => acc.accountId === accountId);
                }
                const org = tenant?.cloudAccounts?.organizations?.find(o => o.orgId === selectedOrgId);
                if (!org) return false;
                return org.id === accountId || org.memberAccounts?.some(m => m.accountId === accountId);
              })}
              summary={summary}
              trendsData={trendsData}
              cloudAccounts={cloudAccounts}
              onAccountSelect={handleAccountSelect}
              onBack={handleBackToTenant}
              title={selectedOrgId === 'standalone' ? 'Standalone Accounts' : tenant?.cloudAccounts?.organizations?.find(o => o.orgId === selectedOrgId)?.alias}
            />
          ) : (
            <CloudMapTenantView
              resources={resources}
              summary={summary}
              tenant={tenant}
              onOrgSelect={handleOrgSelect}
              onStandaloneSelect={handleStandaloneSelect}
            />
          )}
        </div>
      )}


    </div>
  );
};

export default CloudMapPage;
